import { beatTime, secondsToBeat } from '../audio/plan';
import { pressableNote, type Note, type Song } from '../core/chart';
import { deltaMsBetween, holdTooLong, judge } from '../core/judge';
import type { Beat, Millis, Seconds, Verdict, Windows } from '../core/types';

export interface Call {
  readonly note: Note;
  readonly verdict: Verdict;
  readonly deltaMs: Millis;
  readonly at: Seconds;
  readonly held: boolean;
}

export interface Retry {
  readonly retry: Note;
  readonly at: Seconds;
}

export type Release = Call | Retry;

export const RETRY_BEATS: Beat = 1;

export const isRetry = (release: Release): release is Retry => 'retry' in release;

export interface Round {
  readonly song: Song;
  readonly runStart: Seconds;
  readonly holding: boolean;
  readonly holdNote: Note | null;
  readonly cursor: number;
  beatAt(at: Seconds): Beat;
  timeOf(beat: Beat): Seconds;
  apexTime(note: Note): Seconds;
  judged(index: number): boolean;
  press(at: Seconds): Note | null;
  attach(at: Seconds): Note | null;
  release(at: Seconds): Release | null;
  sweep(at: Seconds): Call[];
  drop(): void;
}

export function createRound(song: Song, runStart: Seconds, windows: Windows): Round {
  const done = new Set<number>();
  let cursor = 0;
  let holding = false;
  let spent = false;
  let holdNote: Note | null = null;

  const beatAt = (at: Seconds): Beat => secondsToBeat(at - runStart, song.bpm);
  const timeOf = (beat: Beat): Seconds => beatTime(runStart, beat, song.bpm);
  const apexTime = (note: Note): Seconds => timeOf(note.apexBeat);
  const judged = (index: number): boolean => done.has(index);

  const mark = (note: Note): void => {
    done.add(note.index);
    while (cursor < song.notes.length && done.has(cursor)) cursor++;
  };

  const find = (at: Seconds): Note | null => pressableNote(song.notes, beatAt(at), judged) ?? null;

  return {
    song,
    runStart,
    get holding() {
      return holding;
    },
    get holdNote() {
      return holdNote;
    },
    get cursor() {
      return cursor;
    },
    beatAt,
    timeOf,
    apexTime,
    judged,
    press(at) {
      holding = true;
      spent = false;
      holdNote = find(at);
      return holdNote;
    },
    attach(at) {
      if (!holding || spent || holdNote !== null) return null;
      holdNote = find(at);
      return holdNote;
    },
    release(at) {
      const note = holdNote;
      holding = false;
      spent = false;
      holdNote = null;
      if (note === null) return null;
      if (at < timeOf(note.apexBeat - RETRY_BEATS)) return { retry: note, at };
      const call = judge(note.index, at, apexTime(note), windows);
      mark(note);
      return { note, verdict: call.verdict, deltaMs: call.deltaMs, at, held: true };
    },
    sweep(at) {
      const calls: Call[] = [];
      if (holdNote !== null && holdTooLong(at, apexTime(holdNote), windows)) {
        const note = holdNote;
        holdNote = null;
        spent = holding;
        mark(note);
        calls.push({ note, verdict: 'miss', deltaMs: deltaMsBetween(at, apexTime(note)), at, held: true });
      }
      for (let i = cursor; i < song.notes.length; i++) {
        const note = song.notes[i] as Note;
        if (done.has(note.index) || note === holdNote) continue;
        const deltaMs = deltaMsBetween(at, apexTime(note));
        if (deltaMs <= windows.good) break;
        mark(note);
        calls.push({ note, verdict: 'miss', deltaMs, at, held: false });
      }
      return calls;
    },
    drop() {
      holding = false;
      spent = false;
      holdNote = null;
    },
  };
}
